import { AnimatePresence, motion } from "framer-motion";
import { Check, Flag, Trophy, X } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { trpc } from "../../lib/trpc";
import { cn } from "../../lib/utils";
import { Button } from "../ui";
import type { MatchPlayer } from "./matchHelpers";
import { ProofUploadZone } from "./ProofUploadZone";

type ReportState =
  | { step: "idle" }
  | { step: "confirming-win" }
  | { step: "confirming-loss" }
  | { step: "selecting-winner" }
  | { step: "confirming-team"; teamNum: number }
  | { step: "confirming-player"; userId: string }
  | { step: "reported" };

type ReportResultPanelProps = {
  matchId: string;
  players: MatchPlayer[];
  currentUserId: string;
  teamNames: string[];
  myTeam?: number;
  teamCount: number;
  isFFA: boolean;
  sortedTeams: [number, MatchPlayer[]][];
  totalPlayers: number;
};

export function ReportResultPanel({
  matchId,
  players,
  currentUserId,
  teamNames,
  myTeam,
  teamCount,
  isFFA,
  sortedTeams,
  totalPlayers,
}: ReportResultPanelProps) {
  const [reportState, setReportState] = useState<ReportState>({ step: "idle" });
  const [proofImage, setProofImage] = useState<{ file: File; preview: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const utils = trpc.useUtils();
  const reportMutation = trpc.match.reportResult.useMutation({
    onSuccess: () => {
      void utils.match.get.invalidate();
      void utils.match.getMyActive.invalidate();
    },
    onError: (error) => {
      setReportState({ step: "idle" });
      toast.error(error.message || "Failed to report result");
    },
  });

  // ---- Proof image handlers ----
  const handleProofFile = useCallback((file: File) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are supported");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be under 10MB");
      return;
    }
    const preview = URL.createObjectURL(file);
    setProofImage({ file, preview });
  }, []);

  const handleProofPaste = useCallback(
    (e: React.ClipboardEvent) => {
      const items = e.clipboardData.items;
      for (const item of items) {
        if (item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) handleProofFile(file);
          e.preventDefault();
          return;
        }
      }
    },
    [handleProofFile],
  );

  const handleProofDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (file) handleProofFile(file);
    },
    [handleProofFile],
  );

  const removeProof = useCallback(() => {
    if (proofImage) URL.revokeObjectURL(proofImage.preview);
    setProofImage(null);
  }, [proofImage]);

  // ---- Report handlers ----
  const submitReport = (winningTeam: number, label: string) => {
    setReportState({ step: "reported" });
    toast.success(`Result reported: ${label}`, {
      description: "Waiting for other players to confirm...",
    });
    reportMutation.mutate({ matchId, winningTeam });
  };

  const handleReportWin = () => {
    submitReport(myTeam ?? 1, "Your team won");
  };

  const handleReportLoss = () => {
    // Find the opponent team number (the one that isn't myTeam)
    const opponentTeam = sortedTeams.find(([t]) => t !== myTeam)?.[0] ?? 1;
    submitReport(opponentTeam, "Your team lost");
  };

  const handleReportTeamWin = (teamNum: number) => {
    const name = teamNames[teamNum] ?? `Team ${teamNum}`;
    submitReport(teamNum, `${name} won`);
  };

  const handleReportPlayerWin = (userId: string) => {
    const player = players.find((p) => p.userId === userId);
    const name = player?.username ?? "Unknown";
    // In FFA, each player's team number is their individual team
    const playerTeam = player?.team ?? 1;
    submitReport(playerTeam, `${name} won`);
  };

  const proofProps = {
    proofImage,
    onPaste: handleProofPaste,
    onDrop: handleProofDrop,
    onRemove: removeProof,
    fileInputRef,
  };

  return (
    <div className="relative rounded-xl border border-surface-700/40 bg-surface-800/40 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-500/30 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-b from-brand-500/[0.02] to-transparent pointer-events-none" />

      <div className="relative p-5">
        <div className="flex items-center gap-2 mb-4">
          <Flag className="h-4 w-4 text-brand-400" />
          <h3 className="font-display text-sm font-bold uppercase tracking-wider text-text-primary">
            Report Match Result
          </h3>
        </div>

        <AnimatePresence mode="wait">
          {/* ---- Idle ---- */}
          {reportState.step === "idle" && (
            <motion.div
              key="idle"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2 }}
              className="space-y-3"
            >
              {isFFA ? (
                <FfaPlayerSelection
                  players={players}
                  currentUserId={currentUserId}
                  totalPlayers={totalPlayers}
                  onSelect={(userId) => setReportState({ step: "confirming-player", userId })}
                />
              ) : teamCount === 2 ? (
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setReportState({ step: "confirming-win" })}
                    className="group relative flex flex-col items-center gap-2.5 rounded-lg border border-brand-500/20 bg-brand-500/[0.06] p-5 transition-all duration-200 hover:border-brand-500/40 hover:bg-brand-500/[0.1] hover:shadow-brand-glow-md focus:outline-none focus:ring-2 focus:ring-brand-500/40"
                  >
                    <Trophy className="h-7 w-7 text-brand-400 transition-transform duration-200 group-hover:scale-110" />
                    <span className="font-display text-sm font-bold text-brand-400 uppercase tracking-wide">
                      We Won
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setReportState({ step: "confirming-loss" })}
                    className="group relative flex flex-col items-center gap-2.5 rounded-lg border border-surface-600/30 bg-surface-800/60 p-5 transition-all duration-200 hover:border-surface-500/40 hover:bg-surface-700/60 focus:outline-none focus:ring-2 focus:ring-surface-500/40"
                  >
                    <X className="h-7 w-7 text-surface-400 transition-transform duration-200 group-hover:scale-110" />
                    <span className="font-display text-sm font-bold text-surface-400 uppercase tracking-wide">
                      We Lost
                    </span>
                  </button>
                </div>
              ) : (
                <MultiTeamSelection
                  sortedTeams={sortedTeams}
                  teamNames={teamNames}
                  teamCount={teamCount}
                  myTeam={myTeam}
                  onSelect={(teamNum) => setReportState({ step: "confirming-team", teamNum })}
                />
              )}

              <p className="text-[11px] text-surface-500 text-center leading-relaxed">
                All players must report the same result. Conflicting reports will create a dispute.
              </p>
            </motion.div>
          )}

          {/* ---- Selecting winner (transition state) ---- */}
          {reportState.step === "selecting-winner" && (
            <motion.div
              key="selecting"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2 }}
            >
              <p className="text-sm text-surface-400 text-center py-4">
                Select the winning team above.
              </p>
            </motion.div>
          )}

          {/* ---- Confirming win/loss (2-team) ---- */}
          {(reportState.step === "confirming-win" || reportState.step === "confirming-loss") && (
            <ConfirmationStep
              key="confirming"
              isPositive={reportState.step === "confirming-win"}
              label={
                isFFA
                  ? reportState.step === "confirming-win"
                    ? "Confirm: You won?"
                    : "Confirm: You lost?"
                  : reportState.step === "confirming-win"
                    ? `Confirm: ${teamNames[myTeam ?? 0] ?? "Your team"} won?`
                    : `Confirm: ${teamNames[myTeam ?? 0] ?? "Your team"} lost?`
              }
              proofProps={proofProps}
              onCancel={() => setReportState({ step: "idle" })}
              onConfirm={reportState.step === "confirming-win" ? handleReportWin : handleReportLoss}
            />
          )}

          {/* ---- Confirming team win (3+ teams) ---- */}
          {reportState.step === "confirming-team" && (
            <ConfirmationStep
              key="confirming-team"
              isPositive={reportState.teamNum === myTeam}
              label={`Confirm: ${teamNames[reportState.teamNum] ?? `Team ${reportState.teamNum}`} won?`}
              proofProps={proofProps}
              onCancel={() => setReportState({ step: "idle" })}
              onConfirm={() => handleReportTeamWin(reportState.teamNum)}
            />
          )}

          {/* ---- Confirming player win (FFA) ---- */}
          {reportState.step === "confirming-player" && (
            <PlayerConfirmationStep
              key="confirming-player"
              players={players}
              winnerId={reportState.userId}
              currentUserId={currentUserId}
              proofProps={proofProps}
              onCancel={() => setReportState({ step: "idle" })}
              onConfirm={() => handleReportPlayerWin(reportState.userId)}
            />
          )}

          {/* ---- Reported ---- */}
          {reportState.step === "reported" && (
            <motion.div
              key="reported"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.25 }}
              className="flex items-center gap-3 rounded-lg border border-brand-500/20 bg-brand-500/[0.06] p-4"
            >
              <div className="h-8 w-8 rounded-full bg-brand-500/20 flex items-center justify-center shrink-0">
                <Check className="h-4 w-4 text-brand-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-text-primary">Result Reported</p>
                <p className="text-[11px] text-surface-400 mt-0.5">
                  Waiting for other players to confirm...
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleProofFile(file);
            e.target.value = "";
          }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components (file-private)
// ---------------------------------------------------------------------------

type ProofProps = {
  proofImage: { file: File; preview: string } | null;
  onPaste: (e: React.ClipboardEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onRemove: () => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
};

function FfaPlayerSelection({
  players,
  currentUserId,
  totalPlayers,
  onSelect,
}: {
  players: MatchPlayer[];
  currentUserId: string;
  totalPlayers: number;
  onSelect: (userId: string) => void;
}) {
  return (
    <div className="space-y-2">
      <p className="text-xs text-surface-400 mb-3">Select the player who won:</p>
      <div
        className={cn(
          "grid gap-1.5",
          totalPlayers <= 6 ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-3",
        )}
      >
        {players.map((player) => {
          const isMe = player.userId === currentUserId;
          return (
            <button
              key={player.userId}
              type="button"
              onClick={() => onSelect(player.userId)}
              className={cn(
                "group relative flex items-center gap-2 rounded-lg border p-2.5 transition-all duration-200 text-left focus:outline-none focus:ring-2",
                isMe
                  ? "border-brand-500/25 bg-brand-500/[0.06] hover:border-brand-500/40 hover:bg-brand-500/[0.1] focus:ring-brand-500/40"
                  : "border-surface-600/30 bg-surface-800/60 hover:border-surface-500/40 hover:bg-surface-700/60 focus:ring-surface-500/40",
              )}
            >
              <div className="h-7 w-7 rounded-full bg-surface-700 overflow-hidden shrink-0">
                {player.avatarUrl ? (
                  <img src={player.avatarUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-[10px] font-bold text-surface-400">
                    {(player.username ?? "?").charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p
                  className={cn(
                    "text-xs font-display font-bold truncate",
                    isMe ? "text-brand-300" : "text-text-primary",
                  )}
                >
                  {player.username}
                </p>
                {isMe && <p className="text-[9px] text-brand-500/60">You</p>}
              </div>
              <Trophy
                className={cn(
                  "h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-100",
                  isMe ? "text-brand-400" : "text-surface-400",
                )}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MultiTeamSelection({
  sortedTeams,
  teamNames,
  teamCount,
  myTeam,
  onSelect,
}: {
  sortedTeams: [number, MatchPlayer[]][];
  teamNames: string[];
  teamCount: number;
  myTeam?: number;
  onSelect: (teamNum: number) => void;
}) {
  return (
    <div className="space-y-2">
      <p className="text-xs text-surface-400 mb-3">Select the team that won the match:</p>
      <div
        className={cn("grid gap-2", teamCount <= 4 ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-3")}
      >
        {sortedTeams.map(([teamNum]) => {
          const name = teamNames[teamNum] ?? `Team ${teamNum}`;
          const isOwn = teamNum === myTeam;
          return (
            <button
              key={teamNum}
              type="button"
              onClick={() => onSelect(teamNum)}
              className={cn(
                "group relative flex items-center gap-2.5 rounded-lg border p-3.5 transition-all duration-200 text-left focus:outline-none focus:ring-2",
                isOwn
                  ? "border-brand-500/25 bg-brand-500/[0.06] hover:border-brand-500/40 hover:bg-brand-500/[0.1] focus:ring-brand-500/40"
                  : "border-surface-600/30 bg-surface-800/60 hover:border-surface-500/40 hover:bg-surface-700/60 focus:ring-surface-500/40",
              )}
            >
              <Trophy
                className={cn(
                  "h-5 w-5 shrink-0 transition-transform duration-200 group-hover:scale-110",
                  isOwn ? "text-brand-400" : "text-surface-400",
                )}
              />
              <div className="min-w-0">
                <p
                  className={cn(
                    "text-sm font-display font-bold truncate",
                    isOwn ? "text-brand-300" : "text-text-primary",
                  )}
                >
                  {name}
                </p>
                {isOwn && <p className="text-[10px] text-brand-500/60 mt-0.5">Your team</p>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ConfirmationStep({
  isPositive,
  label,
  proofProps,
  onCancel,
  onConfirm,
}: {
  isPositive: boolean;
  label: string;
  proofProps: ProofProps;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.2 }}
      className="space-y-3"
    >
      <div
        className={cn(
          "flex items-center gap-3 rounded-lg border p-4",
          isPositive
            ? "border-brand-500/20 bg-brand-500/[0.06]"
            : "border-surface-600/30 bg-surface-800/60",
        )}
      >
        {isPositive ? (
          <Trophy className="h-5 w-5 text-brand-400 shrink-0" />
        ) : (
          <X className="h-5 w-5 text-surface-400 shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-text-primary">{label}</p>
          <p className="text-[11px] text-surface-500 mt-0.5">This cannot be undone.</p>
        </div>
      </div>

      <ProofUploadZone {...proofProps} />

      <div className="flex gap-2">
        <Button variant="ghost" className="flex-1" onClick={onCancel}>
          Cancel
        </Button>
        <Button className="flex-1" onClick={onConfirm}>
          <Check className="h-4 w-4 mr-1.5" />
          Confirm
        </Button>
      </div>
    </motion.div>
  );
}

function PlayerConfirmationStep({
  players,
  winnerId,
  currentUserId,
  proofProps,
  onCancel,
  onConfirm,
}: {
  players: MatchPlayer[];
  winnerId: string;
  currentUserId: string;
  proofProps: ProofProps;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const winner = players.find((p) => p.userId === winnerId);
  const isMe = winnerId === currentUserId;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.2 }}
      className="space-y-3"
    >
      <div
        className={cn(
          "flex items-center gap-3 rounded-lg border p-4",
          isMe
            ? "border-brand-500/20 bg-brand-500/[0.06]"
            : "border-surface-600/30 bg-surface-800/60",
        )}
      >
        <div className="h-9 w-9 rounded-full bg-surface-700 overflow-hidden shrink-0">
          {winner?.avatarUrl ? (
            <img src={winner.avatarUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="h-full w-full flex items-center justify-center text-xs font-bold text-surface-400">
              {(winner?.username ?? "?").charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-text-primary">
            Confirm: {winner?.username ?? "Unknown"} won?
          </p>
          <p className="text-[11px] text-surface-500 mt-0.5">
            {isMe ? "Reporting yourself as the winner" : "This cannot be undone."}
          </p>
        </div>
      </div>

      <ProofUploadZone {...proofProps} />

      <div className="flex gap-2">
        <Button variant="ghost" className="flex-1" onClick={onCancel}>
          Cancel
        </Button>
        <Button className="flex-1" onClick={onConfirm}>
          <Check className="h-4 w-4 mr-1.5" />
          Confirm
        </Button>
      </div>
    </motion.div>
  );
}
